import { Modal } from "@components/modal";
import { formatDate, formatTime } from "@helpers/date";
import { formatRubles, formatUsdt } from "@helpers/format";
import { EllipsisText } from "@components/ellipsis-text";
import { Config, DoubleText } from "@components/table";
import { MerchantOrderPayout } from "@features/merchantPayout/api";
import { RowTable } from "@components/row-table";
import { StatusBlock, StatusBlockType } from "@components/status-block";
import { useMerchantPayoutOrders } from "@features/merchantPayout";
import { store } from "@app/store";
import {
  removeFile,
  resetAppeal,
  setMessage,
} from "@features/merchantPayout/slice";
import { useNotification } from "@hooks/use-notification";
import { Button } from "@components/button";
import { FlexLoader } from "@components/loader";
import { useEffect, useState } from "react";
import { CreateAppealRequest } from "@features/appeals/api";
import { PaymentProcessing } from "@features/appeals/components/status";

type Props = {
  isModalOpen: boolean;
  hideModal: () => void;
  data: MerchantOrderPayout;
  isView?: boolean;
};

export const PayoutAppealModal = ({
  hideModal,
  isModalOpen,
  data,
  isView = false,
}: Props) => {
  const { state, getMerchantPayoutOrders, uploadReceipt, createAppeal } =
    useMerchantPayoutOrders();
  const notif = useNotification();
  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.files == null || data.id == null) return;
    const file = event.target.files;
    uploadReceipt(data.id, file).then(() => {
      notif.addNotification("Файл добавлен");
    });
  };

  const config: Config<MerchantOrderPayout> = [
    {
      label: "Номер заявки",
      source: "id",
      renderComponent: (data) => (
        <EllipsisText text={data} copy startLength={10} />
      ),
    },
    {
      label: "Invid заявки",
      source: "invid",
      renderComponent: (data) => (
        <EllipsisText text={data} copy startLength={10} />
      ),
    },
    { label: "Касса", source: "invid", renderComponent: () => "no data" },
    {
      label: "Дата и время создания",
      source: "created_at_unix",
      renderComponent: (data) => (
        <DoubleText
          text={formatTime(new Date(data * 1000))}
          subText={formatDate(new Date(data * 1000))}
        />
      ),
    },
    {
      label: "Дата и время закрытия",
      source: "closed_time_unix",
      renderComponent: (data) => (
        <DoubleText
          text={formatTime(new Date(data * 1000))}
          subText={formatDate(new Date(data * 1000))}
        />
      ),
    },
    {
      label: "Статус заявки",
      source: "status",
      renderComponent: (data) => {
        let text = "";
        let status: StatusBlockType = StatusBlockType.Error;
        switch (data) {
          case "created":
            text = "Создано";
            status = StatusBlockType.Success;
            break;
          case "completed":
            text = "Успешно";
            status = StatusBlockType.Success;
            break;
          case "dispute_open":
            text = "Открыт спор";
            status = StatusBlockType.Orange;
            break;
          case "processing":
          case "pending":
          case "assigned_to_trader":
            text = "Ожидание";
            status = StatusBlockType.Warning;
            break;
          case "under_review_by_support":
            text = "На рассмотрении";
            status = StatusBlockType.Warning;
            break;
          case "failed":
          case "canceled":
            text = "Отклонена";
            status = StatusBlockType.Error;
            break;
        }
        return <StatusBlock text={text} type={status} />;
      },
    },
    { label: "Способ", source: "method" },
    { label: "Реквизиты", source: "requisite" },
    { label: "Банк", source: "bank" },
    { label: "Курс", source: "exchange_rate" },
    {
      label: "Сумма ",
      source: "sum",
      renderComponent: (data, item) => (
        <DoubleText
          text={formatRubles(data)}
          subText={formatUsdt(item.sum_usdt)}
        />
      ),
    },
    {
      label: "Сумма к списанию",
      source: "merchant_spend_rub",
      renderComponent: (data, item) => (
        <DoubleText
          text={formatRubles(data)}
          subText={formatUsdt(item.merchant_spend_usdt)}
        />
      ),
    },
    {
      label: "Файлы",
      source: "bank",
      renderComponent: () => (
        <>
          {state.isReceiptLoading ? (
            "Загрузка..."
          ) : (
            <>
              {state.receiptUrls.map((file) => (
                <div
                  key={file}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    cursor: "pointer",
                  }}
                >
                  <a className="underline" href={file} target="_blank">
                    {file.split("/").pop()}
                  </a>
                  <span
                    style={{ color: "red" }}
                    onClick={() => {
                      store.dispatch(removeFile(file));
                    }}
                  >
                    ×
                  </span>
                </div>
              ))}
              {!isView && (
                <label style={{ cursor: "pointer" }}>
                  Добавить
                  <input
                    type="file"
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                  />
                </label>
              )}
            </>
          )}
        </>
      ),
    },
    {
      label: "Ваше сообщение",
      source: "bank",
      renderComponent: () => (
        <textarea
          disabled={isView}
          value={state.message}
          onChange={(e) => store.dispatch(setMessage(e.target.value))}
        ></textarea>
      ),
    },
  ];

  useEffect(() => {
    store.dispatch(resetAppeal());
  }, [data]);

  const sendData = () => {
    const request: CreateAppealRequest = {
      order: data.id,
      images: state.receiptUrls,
      message: state.message,
      order_type: "pay_out",
    };
    createAppeal(request).then(() => {
      hideModal();
      notif.addNotification("Создано!");
      getMerchantPayoutOrders({ limit: 100, page: 1 });
    });
  };
  const [isDisabled, setIsDisabled] = useState(true);
  useEffect(() => {
    setIsDisabled(state.receiptUrls.length == 0 || state.message.length == 0);
  }, [state.message, state.receiptUrls]);

  const isSendVisible =
    data.status == "completed" && !isView && !state.isAppealLoading;

  return (
    <Modal
      // className={styles.modal}
      isOpen={isModalOpen}
      onClose={hideModal}
      title={isView ? "Апелляция" : "Новая апелляция"}
    >
      {/* {state.lastAppeal?.status === "approved" && !state.loadingByOrder && (
        <PaymentCredited text="Аппеляция принята" />
      )}

      {state.lastAppeal?.status === "canceled" && (
        <PaymentNotCredited text="Аппеляция отменена" />
      )} */}
      {data.status === "dispute_open" && <PaymentProcessing />}

      {state.isAppealLoading ? (
        <FlexLoader style={{ width: "200px", height: "200px" }} />
      ) : (
        <RowTable config={config} data={data} />
      )}

      {isSendVisible && (
        <Button disabled={isDisabled} onClick={sendData}>
          Отправить
        </Button>
      )}
    </Modal>
  );
};
